/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Stack,
  FormErrorMessage,
  Text,
  Checkbox,
  VStack,
  useToast
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { getEmbeddingModels } from '@/services/embedding-models'
import { useAuth0 } from '@auth0/auth0-react'
import CurrentEmbeddingModels from './components/CurrentEmbeddingModels'
import { createAiMetadata } from '@/services/sams'
import CreatingAiMetadataMessage from './components/CreatingAiMetadataMessage'
import NoAvailableEmbeddings from './components/NoAvailableEmbeddings'
import AiMetadataForm from './components/AiMetadataForm'

const AddAiMetadata = ({
  isOpen,
  onClose,
  reloadSam,
  sam
}) => {
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const [availableEmbeddingModels, setAvailableEmbeddingModels] = useState([])
  const toast = useToast()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm({
    defaultValues: {
      embeddingModelIds: []
    }
  })

  const selectedModels = watch('embeddingModelIds') || []

  const getAvailableEmbeddingsModels = async () => {
    const embeddingModels = await getEmbeddingModels(
      { supportedFileTypes: sam.fileType }, getAccessToken
    )
    const samEmbeddingModels = sam.preDealAIStrategies.map(em => em.id)

    setAvailableEmbeddingModels(
      embeddingModels.embeddingModels.filter(
        model => !samEmbeddingModels.includes(model.id)
      )
    )
  }

  useEffect(() => {
    getAvailableEmbeddingsModels()
  }, [])

  const onSubmit = async (data) => {
    try {
      await createAiMetadata(sam.id, data, getAccessToken)

      toast({
        title: 'Success',
        description: 'AI Metadata added successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })

      reset()
      onClose()
      reloadSam()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add AI Metadata. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add AI Metadata</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          { isSubmitting
            ? <CreatingAiMetadataMessage />
            : <Stack spacing={4}>
                <CurrentEmbeddingModels
                  embeddingsModels={sam.preDealAIStrategies}
                />

                { availableEmbeddingModels.length === 0
                  ? <NoAvailableEmbeddings />
                  : <AiMetadataForm
                      availableEmbeddingModels={availableEmbeddingModels}
                      errors={errors}
                      handleSubmit={handleSubmit}
                      onSubmit={onSubmit}
                      register={register}
                    />
                }
              </Stack>
          }
        </ModalBody>
        { !isSubmitting &&
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="ai-metadata-form"
              isLoading={isSubmitting}
              isDisabled={availableEmbeddingModels.length === 0 || selectedModels.length === 0}
            >
              Save Changes
            </Button>
          </ModalFooter>
        }
      </ModalContent>
    </Modal>
  )
}

export default AddAiMetadata
